import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '../auth';
import { profileAPI } from '@/api/profile';
import { handleApiError } from '@/lib/api';

export function Profile() {
  const { user } = useAuth();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isUpdatingTelegram, setIsUpdatingTelegram] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [telegramChatId, setTelegramChatId] = useState(user?.telegram_chat_id || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Новый пароль и подтверждение не совпадают');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('Новый пароль должен содержать минимум 6 символов');
      return;
    }

    setIsChangingPassword(true);

    try {
      await profileAPI.changePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        confirm_password: passwordForm.confirmPassword
      });

      setSuccess('Пароль успешно изменен');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleTelegramUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsUpdatingTelegram(true);

    try {
      await profileAPI.updateSettings({
        telegram_chat_id: telegramChatId || null
      });

      setSuccess('Telegram Chat ID успешно обновлен');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsUpdatingTelegram(false);
    }
  };

  if (!user) {
    return null; // ProtectedRoute должен обработать это
  }

  return (
    <div className="container mx-auto max-w-4xl">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Личный кабинет</h1>
          <p className="text-muted-foreground mt-2">
            Управление настройками профиля и безопасности
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-1 max-w-2xl">
          {/* Смена пароля */}
          <Card>
            <CardHeader>
              <CardTitle>Смена пароля</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Текущий пароль</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Новый пароль</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Подтвердите новый пароль</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                    minLength={6}
                  />
                </div>
                <Button type="submit" disabled={isChangingPassword} className="w-full">
                  {isChangingPassword ? 'Изменение...' : 'Изменить пароль'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Telegram Chat ID для админов */}
        {user.role === 'Администратор' && (
          <Card>
            <CardHeader>
              <CardTitle>Telegram уведомления</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTelegramUpdate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="telegramChatId">Chat ID</Label>
                  <Input
                    id="telegramChatId"
                    type="text"
                    value={telegramChatId}
                    onChange={(e) => setTelegramChatId(e.target.value)}
                    placeholder="Например: 123456789"
                  />
                </div>
                <Button type="submit" disabled={isUpdatingTelegram} className="w-full">
                  {isUpdatingTelegram ? 'Обновление...' : 'Обновить Telegram ID'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Сообщения об ошибках и успехе */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="text-sm text-red-600">
                {error}
              </div>
            </CardContent>
          </Card>
        )}
        {success && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="text-sm text-green-600">
                {success}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
